// app/apps/campsite-explorer/components/ImageGalleryHero.tsx
'use client';

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { FaMapMarkerAlt, FaStar, FaChevronLeft, FaChevronRight, FaExpand, FaTimes, FaCamera } from 'react-icons/fa';
import { GALLERY_MAIN_SIZES, GALLERY_GRID_SIZES, GALLERY_THUMB_SIZES } from '../constants/imageSizes';
import { displayName } from '../lib/displayName';
import AppInstallCard from './AppInstallCard';
import PlaceMapTile from './store/PlaceMapTile';
import { useCaPreview } from '../lib/caPreview';
import { placeMapUrl } from '../lib/placeMap';
import { isOwnMedia, sizedMedia } from '../lib/regionImage';

interface CampsiteInfo {
  name: string;
  location: string;
  rating?: number;
  starClassification?: number;
}

function StarRating({ rating, starClassification }: { rating?: number; starClassification?: number }) {
  if (starClassification) {
    const fullStars = Math.floor(starClassification);
    const hasHalf = starClassification % 1 >= 0.5;
    return (
      <span className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <FaStar
            key={i}
            className={i < fullStars ? 'text-yellow-400' : 'text-white/30'}
            size={14}
          />
        ))}
        {hasHalf && <span className="text-xs text-white/70 ml-0.5">½</span>}
        <span className="text-xs text-white/70 ml-1">AAA Tourism</span>
      </span>
    );
  }
  if (rating) {
    return (
      <span className="flex items-center">
        <FaStar className="text-yellow-400 mr-1" />
        {rating}/5
      </span>
    );
  }
  return null;
}


/**
 * Small "📷 by First L." credit pill overlaid on community/review
 * photos that have been merged into the hero gallery. Official
 * admin/brand photos pass no contributor and render nothing.
 */
function CreditPill({ name, position = 'bl' }: { name?: string; position?: 'tl' | 'bl' }) {
  if (!name) return null;
  const pos = position === 'tl' ? 'top-2 left-2' : 'bottom-2 left-2';
  return (
    <div
      className={`absolute ${pos} z-10 flex items-center gap-1 bg-black/55 text-white text-[11px] font-medium px-2 py-0.5 rounded-full pointer-events-none`}
    >
      <FaCamera className="text-[9px]" aria-hidden="true" />
      <span className="truncate max-w-[140px]">by {name}</span>
    </div>
  );
}

interface ImageGalleryHeroProps {
  images: string[];
  campSiteName: string;
  defaultImage: string;
  campsiteInfo: CampsiteInfo;
  /**
   * Optional per-image contributor credits, aligned by index with
   * `images`. A defined value renders a "📷 by {name}" overlay
   * marking the photo as a community/review upload.
   */
  contributors?: (string | undefined)[];
  /**
   * The app-download promo tile, when the caller appended one to `images`.
   * It is a designed square graphic, not a photo of the campsite, so it
   * renders letterboxed (`object-contain`) and without the name/location
   * overlay — that overlay was cropping and covering its QR code.
   */
  promoImageSrc?: string;
  /**
   * ca_preview only: a listing with no photo of its own shows where it is
   * (a map of the area, pin on the spot) beside its title, never a stock or
   * region photo passed off as the place.
   */
  placeLL?: { lat: number; lng: number };
  /**
   * ca_preview only: the region this listing sits in, as the hero photograph
   * when the listing has none. Always captioned as the region, with the map
   * inset showing exactly where the site is, so it never passes for the site.
   */
  regionPhoto?: { src: string; original?: string; name: string; credit?: string; focus?: string };
  /** ca_preview only: the region's name, as the eyebrow over a photo-less title. */
  regionName?: string;
}

/** Wikimedia originals can be 5000px+; ask for a hero-sized rendition. */
function heroSized(src: string): string {
  if (isOwnMedia(src)) return sizedMedia(src, 1600);
  const m = src.match(/^(https:\/\/upload\.wikimedia\.org\/wikipedia\/commons)\/((?!thumb\/)[0-9a-f]\/[0-9a-f]{2})\/([^/?#]+)$/i);
  return m ? `${m[1]}/thumb/${m[2]}/${m[3]}/1280px-${m[3]}` : src;
}

function RegionPhoto({ src, original, name, credit, focus, onDead }: { src: string; original?: string; name: string; credit?: string; focus?: string; onDead: () => void }) {
  // Try order: hero-sized rendition -> full copy -> original upstream URL.
  const chain = Array.from(new Set([heroSized(src), src, ...(original ? [original] : [])]));
  const [url, setUrl] = useState(() => chain[0]);
  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        className="ca-nophoto-hero__img"
        src={url}
        style={focus ? { objectPosition: focus } : undefined}
        alt=""
        loading="eager"
        fetchPriority="high"
        decoding="async"
        onError={() => {
          const next = chain[chain.indexOf(url) + 1];
          if (next) setUrl(next); else onDead();
        }}
      />
      <span className="ca-nophoto-hero__shade" aria-hidden="true" />
      <span className="ca-nophoto-hero__credit">Pictured: the {name} region{credit ? ` · ${credit}` : ''}</span>
    </>
  );
}

/** Full-width hero map: centred west of the place so its pin sits right of the title card. */
function BleedMap({ lat, lng }: { lat: number; lng: number }) {
  const [failed, setFailed] = useState(false);
  const W = 1024; // the tile server renders at most 2048px (1024 CSS px at @2x)
  const H = 380;
  const Z = 11;
  // Pin at 70% across: shift the centre west by 20% of the width.
  const dLng = (0.2 * W * 360) / (256 * Math.pow(2, Z));
  return (
    <div className="ca-nophoto-hero__bleed" aria-hidden="true">
      {!failed && (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={placeMapUrl(lat, lng - dLng, W, H, Z)} alt="" loading="eager" fetchPriority="high" decoding="async" onError={() => setFailed(true)} />
      )}
      <span className="ca-nophoto-hero__bleedpin">
        <svg width="34" height="44" viewBox="0 0 26 34"><path d="M13 33s11-11.6 11-20A11 11 0 1 0 2 13c0 8.4 11 20 11 20z" fill="#1a68d1" stroke="#fff" strokeWidth="2" /><circle cx="13" cy="13" r="4.2" fill="#fff" /></svg>
      </span>
    </div>
  );
}

function NoPhotoHero(props: ImageGalleryHeroProps) {
  const { campsiteInfo, regionPhoto, placeLL, regionName } = props;
  const [photoDead, setPhotoDead] = useState(false);
  // Only fetched under ca_preview: production keeps its stock hero and never downloads this.
  const preview = useCaPreview();
  const photo = preview && regionPhoto && !photoDead ? regionPhoto : null;
  // ca_preview, no photo: the map of exactly where it is runs the full width
  // of the hero (a cartographic hero, not a half-empty band), the place's pin
  // to the right of the title card.
  const bleed = preview && !photo && placeLL;
  const cls = ["ca-nophoto-hero", "hidden", photo ? "has-photo" : "", placeLL ? "has-map" : "", bleed ? "is-mapbleed" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls}>
      {bleed && placeLL && <BleedMap lat={placeLL.lat} lng={placeLL.lng} />}
      {photo && <RegionPhoto src={photo.src} original={photo.original} name={photo.name} credit={photo.credit} focus={photo.focus} onDead={() => setPhotoDead(true)} />}
      <div className="ca-nophoto-hero__inner max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="ca-nophoto-hero__text">
          {photo ? (
            <p className="ca-nophoto-hero__eyebrow">{photo.name}</p>
          ) : (
            preview && regionName && <p className="ca-nophoto-hero__eyebrow ca-nophoto-hero__eyebrow--ink">{regionName}</p>
          )}
          <div role="heading" aria-level={1} className="ca-nophoto-hero__title">
            {displayName(campsiteInfo.name)}
          </div>
          {campsiteInfo.location && (
            <p className="ca-nophoto-hero__loc">
              <FaMapMarkerAlt aria-hidden="true" /> {campsiteInfo.location}
            </p>
          )}
          <p className="ca-nophoto-hero__note">No photos of this site yet. Been here? Add one from the panel below.</p>
        </div>
        {placeLL && (
          <div className="ca-nophoto-hero__map">
            <PlaceMapTile
              lat={placeLL.lat}
              lng={placeLL.lng}
              width={photo ? 360 : 640}
              height={photo ? 220 : 360}
              zoom={photo ? 9 : 10}
              label={campsiteInfo.location ? `Map · ${campsiteInfo.location}` : "Map"}
              className="absolute inset-0"
              eager
            />
          </div>
        )}
      </div>
    </div>
  );
}

/** Alt text for the promo tile — it is not a photo of the campsite. */
const PROMO_ALT = 'Get the app — scan the QR code to download';

function GalleryHeroInner({
  images,
  campSiteName,
  defaultImage,
  campsiteInfo,
  contributors,
  promoImageSrc,
}: ImageGalleryHeroProps) {
  // Use a mounted state to prevent hydration mismatches
  const [mounted, setMounted] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageError, setImageError] = useState<{ [key: number]: boolean }>({});
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobileGalleryOpen, setIsMobileGalleryOpen] = useState(false);

  // Only render client-side interactive elements after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  const displayImages = images.length > 0 ? images : [defaultImage];
  const totalImages = displayImages.length;

  // The promo tile is matched by src rather than by index: the caller owns
  // the decision to append it, and `images` is de-duplicated upstream, so
  // there is exactly one match when it is present.
  const isPromo = (i: number) => !!promoImageSrc && displayImages[i] === promoImageSrc;

  // SEO: keyword-rich, location-aware alt text for every gallery image.
  // The promo is not a campsite photo and must not claim to be one.
  const _locSuffix = campsiteInfo?.location ? ` in ${campsiteInfo.location}` : '';
  const seoAlt = (i: number) =>
    isPromo(i) ? PROMO_ALT : `${campSiteName}${_locSuffix} — photo ${i + 1}`;
  const creditFor = (i: number) => contributors?.[i];

  // `object-cover` crops to fill, which is right for photos and wrong for a
  // square graphic whose QR code sits near its edge.
  const fitClass = (i: number) =>
    isPromo(i) ? 'object-contain bg-slate-900' : 'object-cover';

  // These arrows sit inside the tap-to-enlarge hero on mobile, so the click
  // must not bubble up and open the lightbox as well.
  const handlePrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? totalImages - 1 : prev - 1));
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentImageIndex((prev) => (prev === totalImages - 1 ? 0 : prev + 1));
  };

  // Picking a thumbnail in the mobile grid opens that photo full-screen.
  // It used to only set the index and drop back to the cropped 300px hero,
  // which left no way to see a photo at full size on a phone.
  const handleThumbnailClick = (index: number) => {
    setCurrentImageIndex(index);
    setIsMobileGalleryOpen(false);
    setIsFullscreen(true);
  };

  const handleImageError = (index: number) => {
    setImageError(prev => ({ ...prev, [index]: true }));
  };

  const hasMultipleImages = totalImages > 1;

  // Keyboard control for the lightbox, plus a scroll lock — without it the
  // page behind the fixed overlay still scrolls under the finger on iOS.
  useEffect(() => {
    if (!isFullscreen && !isMobileGalleryOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullscreen(false);
        setIsMobileGalleryOpen(false);
      } else if (isFullscreen && e.key === 'ArrowLeft') {
        handlePrevious();
      } else if (isFullscreen && e.key === 'ArrowRight') {
        handleNext();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullscreen, isMobileGalleryOpen, totalImages]);

  // Swipe between photos inside the lightbox. 45px matches the threshold the
  // Shopify theme's product gallery uses, so the two feel the same.
  const touchStartX = useRef<number | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const startX = touchStartX.current;
    touchStartX.current = null;
    if (startX === null || totalImages < 2) return;
    const dx = e.changedTouches[0].clientX - startX;
    if (Math.abs(dx) < 45) return;
    if (dx < 0) handleNext();
    else handlePrevious();
  };

  // Show a simplified version during SSR and initial hydration.
  //
  // Its heights MUST match the mounted layout below (500px desktop, 300px
  // mobile). They used to be 400px and 250px, so every campsite page jumped
  // 100px / 50px when hydration swapped the hero in: Search Console field
  // data (5 Oct 2026) had 4,015 mobile URLs and 190 desktop URLs failing CLS,
  // which is the campsite template.
  if (!mounted) {
    return (
      <>
        {/* Desktop Layout - Simplified for SSR */}
        <div className="hidden md:block relative bg-black">
          <div className="max-w-7xl mx-auto">
            {hasMultipleImages ? (
              <div className="grid grid-cols-2 gap-1 h-[500px]">
                {/* Main Image */}
                <div className="relative col-span-1">
                  <Image
                    src={displayImages[0]}
                    alt={seoAlt(0)}
                    fill
                    className="object-cover"
                    sizes={GALLERY_MAIN_SIZES}
                    priority
                    onError={(e) => { (e.target as HTMLImageElement).src = defaultImage; }}
                  />
                  <CreditPill name={creditFor(0)} position="tl" />
                  {/* Campsite Info Overlay on Main Image */}
                  <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                    <h1 className="text-3xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</h1>
                    <div className="flex items-center gap-4 text-white">
                      <span className="flex items-center">
                        <FaMapMarkerAlt className="mr-2 text-red-400" />
                        {campsiteInfo.location}
                      </span>
                      <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
                    </div>
                  </div>
                </div>

                {/* Grid of smaller images — above the fold, so load eagerly.
                    (next/image defaults to loading="lazy", which browsers
                    defer indefinitely in hidden/pre-rendered tabs — the grid
                    rendered as a black half-hero.) */}
                <div className="grid grid-cols-2 gap-1">
                  {displayImages.slice(1, 5).map((image, index) => (
                    <div key={index + 1} className="relative">
                      <Image
                        src={image}
                        alt={seoAlt(index + 1)}
                        fill
                        className={fitClass(index + 1)}
                        sizes={GALLERY_GRID_SIZES}
                        loading="eager"
                        onError={(e) => { (e.target as HTMLImageElement).src = defaultImage; }}
                      />
                      <CreditPill name={creditFor(index + 1)} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Single image - span full width */
              <div className="relative h-[500px]">
                <Image
                  src={displayImages[0]}
                  alt={seoAlt(0)}
                  fill
                  className="object-cover"
                  sizes="100vw"
                  priority
                  onError={(e) => { (e.target as HTMLImageElement).src = defaultImage; }}
                />
                <CreditPill name={creditFor(0)} position="tl" />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <h1 className="text-3xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</h1>
                  <div className="flex items-center gap-4 text-white">
                    <span className="flex items-center">
                      <FaMapMarkerAlt className="mr-2 text-red-400" />
                      {campsiteInfo.location}
                    </span>
                    <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Layout - Simplified for SSR */}
        <div className="md:hidden relative">
          <div className="relative h-[300px] bg-gray-900">
            <Image
              src={displayImages[0]}
              alt={seoAlt(0)}
              fill
              className="object-cover"
              sizes={GALLERY_MAIN_SIZES}
              priority
              onError={(e) => { (e.target as HTMLImageElement).src = defaultImage; }}
            />
            <CreditPill name={creditFor(0)} position="tl" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4">
              <p className="text-2xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</p>
              <div className="flex items-center gap-4 text-white">
                <span className="flex items-center">
                  <FaMapMarkerAlt className="mr-2 text-red-400" />
                  {campsiteInfo.location}
                </span>
                <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Full interactive version after mount
  return (
    <>
      {/* Desktop Layout - Like realestate.com.au */}
      <div className="hidden md:block relative bg-black">
        <div className="max-w-7xl mx-auto">
          {hasMultipleImages ? (
            <div className="grid grid-cols-2 gap-1 h-[500px]">
              {/* Main Image */}
              <div className="relative col-span-1 cursor-pointer" onClick={() => setIsFullscreen(true)}>
                <Image
                  src={imageError[0] ? defaultImage : displayImages[0]}
                  alt={seoAlt(0)}
                  fill
                  className="object-cover"
                  sizes={GALLERY_MAIN_SIZES}
                  priority
                  onError={() => handleImageError(0)}
                />
                <CreditPill name={creditFor(0)} position="tl" />
                {/* Campsite Info Overlay on Main Image */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                  <h1 className="text-3xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</h1>
                  <div className="flex items-center gap-4 text-white">
                    <span className="flex items-center">
                      <FaMapMarkerAlt className="mr-2 text-red-400" />
                      {campsiteInfo.location}
                    </span>
                    <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
                  </div>
                </div>
              </div>

              {/* Grid of smaller images */}
              <div className="grid grid-cols-2 gap-1" role="group" aria-label="Additional campsite photos">
                {displayImages.slice(1, 5).map((image, index) => (
                  <div
                    key={index + 1}
                    className="relative cursor-pointer hover:opacity-90 transition"
                    role="button"
                    tabIndex={0}
                    aria-label={`View photo ${index + 2} of ${totalImages}`}
                    onClick={() => {
                      setCurrentImageIndex(index + 1);
                      setIsFullscreen(true);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCurrentImageIndex(index + 1);
                        setIsFullscreen(true);
                      }
                    }}
                  >
                    <Image
                      src={imageError[index + 1] ? defaultImage : image}
                      alt={seoAlt(index + 1)}
                      fill
                      className={fitClass(index + 1)}
                      sizes={GALLERY_GRID_SIZES}
                      loading="eager"
                      onError={() => handleImageError(index + 1)}
                    />
                    <CreditPill name={creditFor(index + 1)} />
                    {/* Show "View all X photos" on the last image if there are more */}
                    {index === 3 && totalImages > 5 && (
                      <div className="absolute inset-0 bg-black/60 flex items-center justify-center hover:bg-black/70 transition-colors pointer-events-none">
                        <div className="text-center">
                          <FaCamera className="text-white text-3xl mb-2" />
                          <span className="text-white font-medium text-lg">
                            View all {totalImages} photos
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Single image - span full width */
            <div className="relative h-[500px] cursor-pointer" onClick={() => setIsFullscreen(true)}>
              <Image
                src={imageError[0] ? defaultImage : displayImages[0]}
                alt={seoAlt(0)}
                fill
                className="object-cover"
                sizes="100vw"
                priority
                onError={() => handleImageError(0)}
              />
              <CreditPill name={creditFor(0)} position="tl" />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
                <h1 className="text-3xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</h1>
                <div className="flex items-center gap-4 text-white">
                  <span className="flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-red-400" />
                    {campsiteInfo.location}
                  </span>
                  <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden relative">
        <div
          className="relative h-[300px] bg-gray-900 cursor-pointer"
          role="button"
          tabIndex={0}
          aria-label={
            isPromo(currentImageIndex)
              ? 'Get the Camping Australia app'
              : `Enlarge photo ${currentImageIndex + 1} of ${totalImages}`
          }
          onClick={() => !isPromo(currentImageIndex) && setIsFullscreen(true)}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !isPromo(currentImageIndex)) {
              e.preventDefault();
              setIsFullscreen(true);
            }
          }}
        >
          {/* Phones get a tap-to-install card in place of the QR tile — a
              QR is no use on the device that would scan it. */}
          {isPromo(currentImageIndex) ? (
            <AppInstallCard name={displayName(campsiteInfo.name)} />
          ) : (
            <Image
              src={imageError[currentImageIndex] ? defaultImage : displayImages[currentImageIndex]}
              alt={seoAlt(currentImageIndex)}
              fill
              className={fitClass(currentImageIndex)}
              sizes={GALLERY_MAIN_SIZES}
              priority={currentImageIndex === 0}
              onError={() => handleImageError(currentImageIndex)}
            />
          )}
          <CreditPill name={creditFor(currentImageIndex)} position="tl" />

          {/* Navigation buttons */}
          {totalImages > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-full hover:bg-white transition"
                aria-label="Previous image"
              >
                <FaChevronLeft className="text-gray-800" />
              </button>
              <button
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 p-2 rounded-full hover:bg-white transition"
                aria-label="Next image"
              >
                <FaChevronRight className="text-gray-800" />
              </button>
            </>
          )}

          {/* Gradient + name/location. Suppressed on the promo tile: it is a
              designed graphic whose QR code and copy sit under exactly this
              overlay, which made the code unscannable. */}
          {!isPromo(currentImageIndex) && (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 p-4 pointer-events-none">
                <p className="text-2xl font-bold text-white mb-2">{displayName(campsiteInfo.name)}</p>
                <div className="flex items-center gap-4 text-white">
                  <span className="flex items-center">
                    <FaMapMarkerAlt className="mr-2 text-red-400" />
                    {campsiteInfo.location}
                  </span>
                  <StarRating rating={campsiteInfo.rating} starClassification={campsiteInfo.starClassification} />
                </div>
              </div>
            </>
          )}

          {/* Expand affordance — the tap target above is otherwise invisible. */}
          {!isPromo(currentImageIndex) && (
            <span className="absolute top-3 right-3 z-10 bg-black/60 text-white p-2 rounded-full pointer-events-none">
              <FaExpand size={14} aria-hidden="true" />
            </span>
          )}

          {/* Image counter */}
          {totalImages > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMobileGalleryOpen(true);
              }}
              className="absolute bottom-4 right-4 z-10 bg-black/70 text-white px-3 py-1 rounded-full text-sm"
              aria-label={`Photo ${currentImageIndex + 1} of ${totalImages}. Tap to view all photos`}
            >
              {currentImageIndex + 1} / {totalImages}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Gallery Modal.
          Both overlays are portalled to <body>: the detail page wraps this
          component in `.camp-detail`, which sets `isolation: isolate` (needed
          for its z-index:-1 texture). That traps any z-index set in here
          inside that stacking context, and the sticky site nav — painted
          outside it — drew straight over the top of the lightbox. */}
      {isMobileGalleryOpen && createPortal(
        <div className="md:hidden fixed inset-0 bg-black z-[100000] overflow-y-auto" role="dialog" aria-modal="true" aria-label={`Photo gallery for ${campSiteName}`}>
          <div className="sticky top-0 bg-black p-4 flex justify-between items-center">
            <h2 className="text-white font-medium">All Photos ({totalImages})</h2>
            <button
              onClick={() => setIsMobileGalleryOpen(false)}
              className="text-white p-2"
              aria-label="Close photo gallery"
            >
              <FaTimes size={24} aria-hidden="true" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-1 p-1">
            {displayImages.map((image, index) => (
              <div
                key={index}
                className="relative aspect-square cursor-pointer"
                onClick={() => handleThumbnailClick(index)}
              >
                {isPromo(index) ? (
                  <AppInstallCard name={displayName(campsiteInfo.name)} compact />
                ) : (
                  <Image
                    src={imageError[index] ? defaultImage : image}
                    alt={seoAlt(index)}
                    fill
                    className={fitClass(index)}
                    sizes={GALLERY_GRID_SIZES}
                    onError={() => handleImageError(index)}
                  />
                )}
                <CreditPill name={creditFor(index)} />
                {index === currentImageIndex && (
                  <div className="absolute inset-0 ring-4 ring-accent" />
                )}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Fullscreen lightbox — mobile and desktop. On mobile this is the only
          way to see a photo uncropped: the hero is a 300px object-cover box.
          Portalled to <body> for the stacking-context reason noted above. */}
      {isFullscreen && createPortal(
        <div className="fixed inset-0 bg-black z-[100000] block" role="dialog" aria-modal="true" aria-label={`Full screen photo gallery for ${campSiteName}`}>
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex justify-between items-center p-4 bg-black/80">
              <div className="text-white min-w-0">
                <h2 className="text-base md:text-xl font-bold truncate">{displayName(campsiteInfo.name)}</h2>
                <p className="text-sm opacity-80" aria-live="polite">{currentImageIndex + 1} of {totalImages}</p>
              </div>
              <button
                onClick={() => setIsFullscreen(false)}
                className="text-white p-2 hover:bg-white/20 rounded-lg transition flex-shrink-0"
                aria-label="Close full screen gallery"
              >
                <FaTimes size={24} aria-hidden="true" />
              </button>
            </div>

            {/* Main image area */}
            <div
              className="flex-1 relative flex items-center justify-center p-4"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Image
                src={imageError[currentImageIndex] ? defaultImage : displayImages[currentImageIndex]}
                alt={seoAlt(currentImageIndex)}
                fill
                className={isPromo(currentImageIndex) ? 'object-contain hidden md:block' : 'object-contain'}
                sizes="100vw"
                onError={() => handleImageError(currentImageIndex)}
              />
              {/* The lightbox serves phones too: QR on desktop only. */}
              {isPromo(currentImageIndex) && (
                <div className="md:hidden absolute inset-4">
                  <AppInstallCard name={displayName(campsiteInfo.name)} />
                </div>
              )}
              <CreditPill name={creditFor(currentImageIndex)} />

              {/* Navigation */}
              {totalImages > 1 && (
                <>
                  <button
                    onClick={handlePrevious}
                    className="absolute left-4 bg-white/20 p-3 rounded-full hover:bg-white/30 transition"
                    aria-label="Previous photo"
                  >
                    <FaChevronLeft className="text-white" size={24} aria-hidden="true" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-4 bg-white/20 p-3 rounded-full hover:bg-white/30 transition"
                    aria-label="Next photo"
                  >
                    <FaChevronRight className="text-white" size={24} aria-hidden="true" />
                  </button>
                </>
              )}
            </div>

            {/* Thumbnail strip */}
            <div className="bg-black/80 p-4">
              <div className="flex gap-2 overflow-x-auto" role="group" aria-label="Photo thumbnails">
                {displayImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    aria-label={`View photo ${index + 1}${index === currentImageIndex ? ' (current)' : ''}`}
                    aria-current={index === currentImageIndex ? 'true' : undefined}
                    className={`relative flex-shrink-0 w-20 h-20 ${
                      index === currentImageIndex ? 'ring-2 ring-white' : 'opacity-60 hover:opacity-100'
                    } transition`}
                  >
                    <Image
                      src={imageError[index] ? defaultImage : image}
                      alt={seoAlt(index)}
                      fill
                      className={fitClass(index)}
                      sizes={GALLERY_THUMB_SIZES}
                      onError={() => handleImageError(index)}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}





/**
 * ca_preview only: a listing with no photo of its own gets a quiet title band
 * instead of the generic stock campfire photo, which read as a template when
 * it repeated across pages. CSS (aaa-store.css, html.ca-preview) swaps the
 * two, so production and EscapeTo render exactly as before.
 */
export default function ImageGalleryHero(props: ImageGalleryHeroProps) {
  if (props.images.length > 0) return <GalleryHeroInner {...props} />;
  return (
    <>
      <div className="ca-stock-hero">
        <GalleryHeroInner {...props} />
      </div>
      <NoPhotoHero {...props} />
    </>
  );
}
